import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Domain, Role } from "@rrid/shared-types";
import { api } from "../lib/api.js";

export interface DevUser {
  email: string;
  name: string;
  roles: Role[];
  domains: Domain[];
}

interface DevLoadSnapshotResponse {
  snapshot: string;
  users: DevUser[];
}

async function fetchDevUsers(): Promise<DevUser[]> {
  try {
    return await api.get<DevUser[]>("/auth/dev-users");
  } catch {
    return [];
  }
}

/** Users in the currently loaded local.db (no reseed). Used by the header role switcher. */
export function useDevUsersList() {
  return useQuery({
    queryKey: ["dev-users"],
    queryFn: fetchDevUsers,
    retry: false,
    staleTime: 30_000,
  });
}

/** Load a snapshot at runtime and return its personas (login screen). */
export function useDevUsersForSnapshot(snapshotId: string | null) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: ["dev-users", snapshotId],
    queryFn: async () => {
      if (!snapshotId) return [] as DevUser[];
      const loaded = await api.post<DevLoadSnapshotResponse>("/auth/dev-load-snapshot", { snapshot: snapshotId });
      qc.setQueryData(["dev-users"], loaded.users);
      qc.setQueryData(["dev-snapshots"], (prev: { active: string | null; snapshots: unknown[] } | undefined) =>
        prev ? { ...prev, active: loaded.snapshot } : prev,
      );
      return loaded.users;
    },
    enabled: !!snapshotId,
    retry: false,
    staleTime: 30_000,
  });
}

/** Imperatively reload a snapshot (e.g. from just seed-* via a future admin action). */
export function useLoadDevSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshot: string) =>
      api.post<DevLoadSnapshotResponse>("/auth/dev-load-snapshot", { snapshot }),
    onSuccess: (data) => {
      qc.setQueryData(["dev-users", data.snapshot], data.users);
      qc.setQueryData(["dev-users"], data.users);
      qc.setQueryData(["dev-snapshots"], (prev: { active: string | null; snapshots: unknown[] } | undefined) =>
        prev ? { ...prev, active: data.snapshot } : prev,
      );
    },
  });
}
